import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from './firebase';
import Compressor from 'compressorjs';
import './GuestPage.css';
import logo from './assets/logo.png';

export default function GuestPage() {
  const { code } = useParams();
  const [album, setAlbum] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [message, setMessage] = useState('');
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const [touchStartX, setTouchStartX] = useState(null);
  const [touchEndX, setTouchEndX] = useState(null);

  // Load album info
  useEffect(() => {
    async function fetchAlbum() {
      const albumsRef = collection(db, 'albums');
      const q = query(albumsRef, where('code', '==', code));
      const snapshot = await getDocs(q);
      if (!snapshot.empty) {
        setAlbum(snapshot.docs[0].data());
      } else {
        setMessage('Album not found.');
      }
    }
    fetchAlbum();
  }, [code]);

  // Load photos
  useEffect(() => {
    async function fetchPhotos() {
      if (!album) return;
      const photosRef = collection(db, 'photos');
      const q = query(photosRef, where('albumCode', '==', code));
      const snapshot = await getDocs(q);
      const list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setPhotos(list);
      setIsLoading(false);
    }
    fetchPhotos();
  }, [album, code]);

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      // Compress the image
      const compressedFile = await new Promise((resolve, reject) => {
        new Compressor(file, {
          quality: 0.6, // 60% quality for compression
          maxWidth: 1200, // Maximum width
          maxHeight: 1200, // Maximum height
          mimeType: 'image/jpeg', // Convert to JPEG
          success(result) {
            resolve(result);
          },
          error(err) {
            reject(err);
          },
        });
      });

      // Upload to Firebase Storage
      const storageRef = ref(storage, `photos/${code}/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, compressedFile);

      // Get download URL
      const downloadUrl = await getDownloadURL(storageRef);

      // Save in Firestore
      const docRef = await addDoc(collection(db, 'photos'), {
        albumCode: code,
        url: downloadUrl,
        name: file.name,
        uploadedAt: serverTimestamp(),
        storagePath: storageRef.fullPath, // Save storage path for deletion
      });

      // Update UI state
      setPhotos(prev => [...prev, { id: docRef.id, url: downloadUrl, name: file.name }]);
      setMessage('Upload successful!');
    } catch (err) {
      setMessage('Upload failed: ' + err.message);
    }
  };

  const isVideo = (url) => /\.(mp4|webm|ogg|mov)$/i.test(url);

  function handleTouchStart(e) {
    setTouchStartX(e.changedTouches[0].screenX);
  }

  function handleTouchMove(e) {
    setTouchEndX(e.changedTouches[0].screenX);
  }

  function handleTouchEnd() {
    if (touchStartX === null || touchEndX === null) return;
    const diff = touchStartX - touchEndX;
    const swipeThreshold = 50;
    if (diff > swipeThreshold) {
      setLightboxIndex((lightboxIndex + 1) % photos.length);
    } else if (diff < -swipeThreshold) {
      setLightboxIndex((lightboxIndex + photos.length - 1) % photos.length);
    }
    setTouchStartX(null);
    setTouchEndX(null);
  }

  useEffect(() => {
    function handleKeyDown(e) {
      if (lightboxIndex === null) return;
      if (e.key === 'ArrowRight') {
        setLightboxIndex((lightboxIndex + 1) % photos.length);
      } else if (e.key === 'ArrowLeft') {
        setLightboxIndex((lightboxIndex + photos.length - 1) % photos.length);
      } else if (e.key === 'Escape') {
        setLightboxIndex(null);
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxIndex, photos.length]);

  // Show error message
  if (message === 'Album not found.')
    return <p style={{ textAlign: 'center', marginTop: 60, fontSize: 18 }}>{message}</p>;

  // Show loading screen
  if (isLoading) {
    return (
      <div className="loading-container">
        <img
          src={logo}
          alt="Formal Invites Logo"
          className="site-logo"
          style={{ width: '120px', marginBottom: '20px' }}
        />
        <div className="loading-spinner"></div>
        <p className="loading-text">Loading your event gallery...</p>
      </div>
    );
  }

  return (
    <div className="guest-container">
      <header className="site-header">
        <img
          src={logo}
          alt="Website Logo"
          className="site-logo"
          onError={e => (e.currentTarget.style.display = 'none')}
        />
      </header>

      <section className="event-info-upload">
        <h1 className="event-name">{album.name}</h1>
        <p className="event-date">{formatDateLocal(album.date)}</p>

        {album.bannerUrl && (
          <img
            src={album.bannerUrl}
            alt={`${album.name} Banner`}
            style={{
              width: '100%',
              maxHeight: '300px',
              objectFit: 'cover',
              borderRadius: '16px',
              marginBottom: '30px',
              boxShadow: '0 8px 25px rgba(131, 58, 180, 0.1)',
            }}
          />
        )}

        <div className="upload-section">
          <label htmlFor="file-upload" className="custom-file-upload">
            Upload to Guestbook
          </label>
          <input
            id="file-upload"
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
        </div>

        {message && (
          <p
            className="message"
            style={{ color: message.includes('failed') ? '#e74c3c' : '#27ae60' }}
          >
            {message}
          </p>
        )}
      </section>

      <div className="live-feed-container">
        <h2 className="live-feed-title">Live Feed</h2>
        <div className="photo-grid">
          {photos.map((photo, i) => (
            <div
              key={photo.id}
              className="photo-thumb"
              onClick={() => setLightboxIndex(i)}
              title={photo.name || 'View'}
            >
              {isVideo(photo.url) ? (
                <video
                  src={photo.url}
                  controls
                  preload="metadata"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                <img src={photo.url} alt={photo.name} loading="lazy" />
              )}
            </div>
          ))}
        </div>
      </div>

      {lightboxIndex !== null && (
        <div
          className="lightbox-overlay"
          onClick={() => setLightboxIndex(null)}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <button
            className="lightbox-close"
            onClick={(e) => {
              e.stopPropagation();
              setLightboxIndex(null);
            }}
            aria-label="Close"
          >
            ×
          </button>

          <button
            className="lightbox-nav left"
            onClick={e => {
              e.stopPropagation();
              setLightboxIndex((lightboxIndex + photos.length - 1) % photos.length);
            }}
            aria-label="Previous"
          >
            ‹
          </button>

          <button
            className="lightbox-nav right"
            onClick={e => {
              e.stopPropagation();
              setLightboxIndex((lightboxIndex + 1) % photos.length);
            }}
            aria-label="Next"
          >
            ›
          </button>

          {isVideo(photos[lightboxIndex].url) ? (
            <video
              src={photos[lightboxIndex].url}
              controls
              autoPlay
              className="lightbox-image"
              onClick={e => e.stopPropagation()}
            />
          ) : (
            <img
              src={photos[lightboxIndex].url}
              alt={photos[lightboxIndex].name}
              className="lightbox-image"
              onClick={e => e.stopPropagation()}
            />
          )}
        </div>
      )}

      <footer
        style={{
          marginTop: '2rem',
          padding: '1rem 0',
          textAlign: 'center',
          fontSize: '0.9rem',
          color: '#555',
          borderTop: '1px solid #ddd',
        }}
      >
        Created by{' '}
        <a
          href="https://get-gs.com"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: '#0070f3', textDecoration: 'none' }}
        >
          GS
        </a>
      </footer>
    </div>
  );
}

function formatDateLocal(dateStr) {
  if (!dateStr) return '';
  const localDate = new Date(dateStr + 'T00:00:00');
  return localDate.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}
